import React, { useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GraduateAdminEditorApi } from '../services/GraduationApi';
import { GraduateDetails } from './GraduateOverview';
import GraduateCourses from './GraduateCourses';
import GraduateCapstoneApprovals from './GraduateCapstoneApprovals';
import { useAuthSession, useCanEditGraduateApplication } from './StudentWorkspace';

export default function GraduateApplicationAdminEditor() {
  const allowed = useCanEditGraduateApplication();
  const account = useAuthSession();
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const applicationId = Number(id);
  const [step, setStep] = useState<'application' | 'courses' | 'completion'>('application');
  const [revision, setRevision] = useState(0);
  const [saved, setSaved] = useState(false);
  if (!allowed) return <Navigate to="/" replace />;
  if (!Number.isSafeInteger(applicationId) || applicationId <= 0) return <Navigate to="/admin/graduate" replace />;
  const close = () => navigate(`/admin/graduate${params.get('list') ? `?${params.get('list')}` : ''}`);
  const save = () => { setSaved(true); setRevision(value => value + 1); };
  return <div key={`${account?.starId}:${applicationId}`}>
    <Alert variant="info">Editing as an administrator. Changes are recorded under your account.</Alert>
    {saved && <Alert variant="success" role="status">Application changes saved.</Alert>}
    <div role="group" aria-label="Application editing sections" className="d-flex flex-wrap gap-2 mb-3">
      <Button variant={step === 'application' ? 'primary' : 'outline-primary'} aria-pressed={step === 'application'} onClick={() => setStep('application')}>Application details</Button>
      <Button variant={step === 'courses' ? 'primary' : 'outline-primary'} aria-pressed={step === 'courses'} onClick={() => setStep('courses')}>Courses</Button>
      <Button variant={step === 'completion' ? 'primary' : 'outline-primary'} aria-pressed={step === 'completion'} onClick={() => setStep('completion')}>Capstone and faculty approvers</Button>
      <Button variant="outline-secondary" onClick={close}>Back to graduate applications</Button>
    </div>
    {step === 'application' && <GraduateDetails key={`application:${applicationId}:${revision}`} api={GraduateAdminEditorApi} adminMode
      id={applicationId} canWrite canApplyForDegree initialEdit onClose={close} onSaved={save}
      onCourses={() => setStep('courses')} onCompletion={() => setStep('completion')} />}
    {step === 'courses' && <GraduateCourses key={`courses:${applicationId}:${revision}`} api={GraduateAdminEditorApi}
      applicationId={applicationId} canWrite startEditing onDone={close} onSaved={save}
      onEditApplication={() => setStep('application')} onEditCapstone={() => setStep('completion')} />}
    {step === 'completion' && <GraduateCapstoneApprovals key={`completion:${applicationId}:${revision}`} api={GraduateAdminEditorApi} adminMode
      applicationId={applicationId} canWrite startEditing onDone={didSave => didSave ? save() : close()}
      onEditApplication={() => setStep('application')} onEditCourses={() => setStep('courses')} />}
  </div>;
}
